import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import AdminLayout from "@/components/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ArrowLeft, Plus, Trash2, Save, Copy } from "lucide-react";

const CALCULATION_METHODS = [
  { value: "SAME", label: "Same as Shipping Partner" },
  { value: "ADD_PER_KG", label: "Add Per KG" },
  { value: "MARKUP_PERCENT_PER_KG", label: "Markup % Per KG" },
  { value: "ADD_FIXED_TOTAL", label: "Add Fixed Amount" },
  { value: "MARKUP_PERCENT_TOTAL", label: "Markup % on Total" },
  { value: "CALCULATED_FROM_PARAMETER", label: "Calculate from Another Parameter" },
  { value: "FIXED", label: "Fixed Value" },
  { value: "MULTIPLY_BY_WEIGHT", label: "Multiply by Weight" },
  { value: "CONDITIONAL", label: "Conditional (Weight-based Rules)" }
];

const FWD_PARAMETERS = [
  "base_freight_charge",
  "fuel_surcharge",
  "insurance_rov",
  "oda_fm",
  "oda_lm",
  "fm",
  "lm",
  "green",
  "fuel_hike",
  "markup",
  // Meta charges (handling charges breakdown)
  "pod",
  "demurrage",
  "sun",
  "hand",
  "add_man_pwr",
  "add_cost",
  "mathadi_un",
  "adh_vhl",
  "reattempt",
  "appt_chg",
  "chq",
  "csd",
  "cod",
  "sp_dlv_area",
  "add_machine"
];

const CommercialStructure = ({ user, onLogout }) => {
  const { sellerId } = useParams();
  const navigate = useNavigate();
  
  const [seller, setSeller] = useState(null);
  const [shippingPartners, setShippingPartners] = useState([]);
  const [selectedPartnerId, setSelectedPartnerId] = useState("");
  const [structure, setStructure] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showCloneDialog, setShowCloneDialog] = useState(false);
  const [cloning, setCloning] = useState(false);
  const [allSellers, setAllSellers] = useState([]);
  const [selectedTargetSellers, setSelectedTargetSellers] = useState([]);
  const [targetShippingPartner, setTargetShippingPartner] = useState('');
  const [overwriteExisting, setOverwriteExisting] = useState(false);
  
  // Form state
  const [minChargedWt, setMinChargedWt] = useState(20);
  const [fwdCharges, setFwdCharges] = useState({});
  const [rtoCharges, setRtoCharges] = useState({});
  const [customCharges, setCustomCharges] = useState([]);
  const [managementFee, setManagementFee] = useState({ enabled: false, percentage: 0 });
  const [fwdGstPercent, setFwdGstPercent] = useState(18);
  const [rtoGstPercent, setRtoGstPercent] = useState(18);

  useEffect(() => {
    fetchData();
  }, [sellerId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Fetch seller and commercial structures
      const sellerRes = await axios.get(`${API}/commercial-structures/seller/${sellerId}`);
      setSeller(sellerRes.data);
      
      // Fetch shipping partners
      const partnersRes = await axios.get(`${API}/b2b-shipping-partner/`);
      setShippingPartners(partnersRes.data);
      
      // Fetch all sellers for cloning
      const sellersRes = await axios.get(`${API}/sellers-org/`);
      setAllSellers(sellersRes.data.filter(s => s.seller_id !== sellerId)); // Exclude current seller
      
      setLoading(false);
    } catch (error) {
      toast.error("Failed to load data");
      setLoading(false);
    }
  };

  const loadStructure = (partnerId) => {
    const found = seller?.commercial_structures?.find(
      s => s.shipping_partner_id === partnerId
    );
    
    if (found) {
      setStructure(found);
      setMinChargedWt(found.min_charged_wt || 20);
      setFwdCharges(found.fwd_charges || {});
      setRtoCharges(found.rto_charges || {});
      setCustomCharges(found.custom_charges || []);
      setManagementFee(found.management_fee || { enabled: false, percentage: 0 });
      setFwdGstPercent(found.fwd_charges?.gst_percent || 18);
      setRtoGstPercent(found.rto_charges?.gst_percent || 18);
    } else {
      // Initialize new structure
      setStructure(null);
      setMinChargedWt(20);
      setFwdCharges({});
      setRtoCharges({});
      setCustomCharges([]);
      setManagementFee({ enabled: false, percentage: 0 });
      setFwdGstPercent(18);
      setRtoGstPercent(18);
    }
  };

  const handlePartnerChange = (partnerId) => {
    setSelectedPartnerId(partnerId);
    loadStructure(partnerId);
  };

  const updateParamConfig = (type, param, field, value) => {
    if (type === 'fwd') {
      setFwdCharges(prev => ({
        ...prev,
        [param]: {
          ...prev[param],
          [field]: value
        }
      }));
    } else {
      setRtoCharges(prev => ({
        ...prev,
        [param]: {
          ...prev[param],
          [field]: value
        }
      }));
    }
  };

  const addCustomCharge = () => {
    setCustomCharges([...customCharges, {
      id: `custom-${Date.now()}`,
      name: "",
      applies_to: "FWD",
      tax_treatment: "PRE_TAX",
      method: "FIXED",
      value: 0,
      rules: []
    }]);
  };

  const removeCustomCharge = (index) => {
    setCustomCharges(customCharges.filter((_, i) => i !== index));
  };

  const updateCustomCharge = (index, field, value) => {
    const updated = [...customCharges];
    updated[index][field] = value;
    setCustomCharges(updated);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      
      if (!selectedPartnerId) {
        toast.error("Please select a shipping partner");
        setSaving(false);
        return;
      }
      
      const payload = {
        shipping_partner_id: selectedPartnerId,
        min_charged_wt: minChargedWt,
        fwd_charges: {
          ...fwdCharges,
          gst_percent: fwdGstPercent
        },
        rto_charges: {
          ...rtoCharges,
          gst_percent: rtoGstPercent
        },
        custom_charges: customCharges,
        management_fee: managementFee
      };
      
      if (structure) {
        // Update existing
        await axios.put(
          `${API}/commercial-structures/seller/${sellerId}/structure/${structure.id}`,
          payload
        );
        toast.success("Commercial structure updated successfully");
      } else {
        // Create new
        await axios.post(
          `${API}/commercial-structures/seller/${sellerId}`,
          payload
        );
        toast.success("Commercial structure created successfully");
      }
      
      // Refresh data
      await fetchData();
      loadStructure(selectedPartnerId);
      
      setSaving(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save commercial structure");
      setSaving(false);
    }
  };

  const handleClone = async () => {
    if (selectedTargetSellers.length === 0) {
      toast.error("Please select at least one target seller");
      return;
    }
    
    if (!targetShippingPartner) {
      toast.error("Please select a shipping partner for target sellers");
      return;
    }
    
    try {
      setCloning(true);
      
      const cloneData = {
        target_sellers: selectedTargetSellers.map(sellerId => ({
          seller_id: sellerId,
          shipping_partner_id: targetShippingPartner
        })),
        overwrite: overwriteExisting
      };
      
      const response = await axios.post(
        `${API}/commercial-structures/seller/${sellerId}/structure/${structure.id}/clone`,
        cloneData
      );
      
      const { results, errors } = response.data;
      
      if (results.length > 0) {
        toast.success(`Successfully cloned to ${results.length} seller(s)`);
      }
      
      if (errors.length > 0) {
        errors.forEach(err => {
          toast.error(`${err.seller_name || err.seller_id}: ${err.error}`);
        });
      }
      
      // Reset form
      setSelectedTargetSellers([]);
      setTargetShippingPartner('');
      setOverwriteExisting(false);
      setShowCloneDialog(false);
      setCloning(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to clone structure");
      setCloning(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout user={user} onLogout={onLogout}>
        <div className="flex items-center justify-center h-64">
          <p>Loading...</p>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout user={user} onLogout={onLogout}>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="outline" onClick={() => navigate('/admin/sellers')}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Sellers
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Commercial Structure</h1>
              <p className="text-slate-600">{seller?.brand_name}</p>
            </div>
          </div>
          <div className="flex gap-3">
            <Button 
              variant="outline" 
              onClick={() => setShowCloneDialog(true)} 
              disabled={!structure}
            >
              <Copy className="w-4 h-4 mr-2" />
              Clone Structure
            </Button>
            <Button onClick={handleSave} disabled={saving || !selectedPartnerId} style={{backgroundColor: '#00A699'}}>
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Saving...' : 'Save Structure'}
            </Button>
          </div>
        </div>

        {/* Shipping Partner Selection */}
        <Card>
          <CardHeader>
            <CardTitle>Select Shipping Partner</CardTitle>
            <CardDescription>Choose a shipping partner to configure billing structure</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div>
                <Label>Shipping Partner *</Label>
                <Select value={selectedPartnerId} onValueChange={handlePartnerChange}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select shipping partner" />
                  </SelectTrigger>
                  <SelectContent>
                    {shippingPartners.map(partner => (
                      <SelectItem key={partner.id} value={partner.id}>
                        {partner.name}
                        {seller?.commercial_structures?.find(s => s.shipping_partner_id === partner.id) && 
                          " ✓ (Configured)"}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {selectedPartnerId && (
                <div>
                  <Label>Minimum Charged Weight (kg)</Label>
                  <Input
                    type="number"
                    value={minChargedWt}
                    onChange={(e) => setMinChargedWt(parseFloat(e.target.value))}
                    step="0.1"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    If actual weight is less than this, this weight will be used for billing
                  </p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {selectedPartnerId && (
          <>
            {/* Forward & RTO Charges Configuration */}
            <Card>
              <CardHeader>
                <CardTitle>Charges Configuration</CardTitle>
              </CardHeader>
              <CardContent>
                <Tabs defaultValue="fwd">
                  <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger value="fwd">Forward Charges</TabsTrigger>
                    <TabsTrigger value="rto">RTO Charges</TabsTrigger>
                  </TabsList>
                  
                  <TabsContent value="fwd" className="space-y-4 mt-4">
                    {FWD_PARAMETERS.map(param => (
                      <ParameterConfig
                        key={param}
                        param={param}
                        config={fwdCharges[param] || {}}
                        onChange={(field, value) => updateParamConfig('fwd', param, field, value)}
                      />
                    ))}
                    
                    <div>
                      <Label>GST Percentage</Label>
                      <Input
                        type="number"
                        value={fwdGstPercent}
                        onChange={(e) => setFwdGstPercent(parseFloat(e.target.value))}
                        step="0.1"
                      />
                    </div>
                  </TabsContent>
                  
                  <TabsContent value="rto" className="space-y-4 mt-4">
                    {FWD_PARAMETERS.map(param => (
                      <ParameterConfig
                        key={param}
                        param={param}
                        config={rtoCharges[param] || {}}
                        onChange={(field, value) => updateParamConfig('rto', param, field, value)}
                      />
                    ))}
                    
                    <div>
                      <Label>GST Percentage</Label>
                      <Input
                        type="number"
                        value={rtoGstPercent}
                        onChange={(e) => setRtoGstPercent(parseFloat(e.target.value))}
                        step="0.1"
                      />
                    </div>
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>

            {/* Custom Charges */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Custom Charges</CardTitle>
                    <CardDescription>Add custom charges like LR_Charge, Documentation Fee, etc.</CardDescription>
                  </div>
                  <Button onClick={addCustomCharge} size="sm" style={{backgroundColor: '#00A699'}}>
                    <Plus className="w-4 h-4 mr-2" />
                    Add Custom Charge
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {customCharges.map((charge, index) => (
                    <Card key={charge.id} className="border">
                      <CardContent className="pt-6">
                        <div className="space-y-4">
                          <div className="flex items-center justify-between">
                            <h4 className="font-semibold">Custom Charge {index + 1}</h4>
                            <Button
                              variant="destructive"
                              size="sm"
                              onClick={() => removeCustomCharge(index)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                          
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <Label>Charge Name</Label>
                              <Input
                                value={charge.name}
                                onChange={(e) => updateCustomCharge(index, 'name', e.target.value)}
                                placeholder="e.g., LR_Charge"
                              />
                            </div>
                            
                            <div>
                              <Label>Applies To</Label>
                              <Select
                                value={charge.applies_to}
                                onValueChange={(value) => updateCustomCharge(index, 'applies_to', value)}
                              >
                                <SelectTrigger>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="FWD">Forward</SelectItem>
                                  <SelectItem value="RTO">RTO</SelectItem>
                                  <SelectItem value="BOTH">Both</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            
                            <div>
                              <Label>Tax Treatment</Label>
                              <Select
                                value={charge.tax_treatment}
                                onValueChange={(value) => updateCustomCharge(index, 'tax_treatment', value)}
                              >
                                <SelectTrigger>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="PRE_TAX">Pre-Tax (Before GST)</SelectItem>
                                  <SelectItem value="POST_TAX">Post-Tax (After GST)</SelectItem>
                                </SelectContent>
                              </Select>
                              <p className="text-xs text-slate-500 mt-1">
                                {charge.tax_treatment === 'PRE_TAX' 
                                  ? 'GST will be calculated on this charge'
                                  : 'Added after GST calculation'}
                              </p>
                            </div>
                            
                            <div>
                              <Label>Method</Label>
                              <Select
                                value={charge.method}
                                onValueChange={(value) => updateCustomCharge(index, 'method', value)}
                              >
                                <SelectTrigger>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="FIXED">Fixed Amount</SelectItem>
                                  <SelectItem value="MULTIPLY_BY_WEIGHT">Multiply by Weight</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            
                            <div className="col-span-2">
                              <Label>Value</Label>
                              <Input
                                type="number"
                                value={charge.value}
                                onChange={(e) => updateCustomCharge(index, 'value', parseFloat(e.target.value))}
                                step="0.01"
                              />
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                  
                  {customCharges.length === 0 && (
                    <p className="text-center text-slate-500 py-8">
                      No custom charges added. Click "Add Custom Charge" to create one.
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Management Fee */}
            <Card>
              <CardHeader>
                <CardTitle>Management Fee</CardTitle>
                <CardDescription>Add a percentage markup on the final total amount</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="management-fee">Enable Management Fee</Label>
                    <Switch
                      id="management-fee"
                      checked={managementFee.enabled}
                      onCheckedChange={(checked) => setManagementFee({ ...managementFee, enabled: checked })}
                    />
                  </div>
                  
                  {managementFee.enabled && (
                    <div>
                      <Label>Management Fee Percentage (%)</Label>
                      <Input
                        type="number"
                        value={managementFee.percentage}
                        onChange={(e) => setManagementFee({ ...managementFee, percentage: parseFloat(e.target.value) })}
                        step="0.1"
                      />
                      <p className="text-xs text-slate-500 mt-1">
                        Final Amount = Total + (Total × {managementFee.percentage}%)
                      </p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </div>

      {/* Clone Dialog */}
      <Dialog open={showCloneDialog} onOpenChange={setShowCloneDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Clone Commercial Structure</DialogTitle>
            <DialogDescription>
              Copy "{structure?.shipping_partner_name}" billing structure from {seller?.brand_name} to other sellers
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Target Sellers Selection */}
            <div>
              <Label>Target Sellers *</Label>
              <div className="border rounded-lg p-4 max-h-48 overflow-y-auto space-y-2">
                {allSellers.map(targetSeller => (
                  <div key={targetSeller.seller_id} className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id={`seller-${targetSeller.seller_id}`}
                      checked={selectedTargetSellers.includes(targetSeller.seller_id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedTargetSellers([...selectedTargetSellers, targetSeller.seller_id]);
                        } else {
                          setSelectedTargetSellers(selectedTargetSellers.filter(id => id !== targetSeller.seller_id));
                        }
                      }}
                      className="w-4 h-4"
                    />
                    <Label htmlFor={`seller-${targetSeller.seller_id}`} className="cursor-pointer font-normal">
                      {targetSeller.brand_name} ({targetSeller.seller_id})
                    </Label>
                  </div>
                ))}
                {allSellers.length === 0 && (
                  <p className="text-sm text-slate-500 text-center py-2">No other sellers available</p>
                )}
              </div>
              <p className="text-xs text-slate-500 mt-1">
                Selected: {selectedTargetSellers.length} seller(s)
              </p>
            </div>

            {/* Target Shipping Partner */}
            <div>
              <Label>Shipping Partner for Target Sellers *</Label>
              <Select value={targetShippingPartner} onValueChange={setTargetShippingPartner}>
                <SelectTrigger>
                  <SelectValue placeholder="Select shipping partner" />
                </SelectTrigger>
                <SelectContent>
                  {shippingPartners.map(partner => (
                    <SelectItem key={partner.id} value={partner.id}>
                      {partner.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500 mt-1">
                The structure will be configured for this shipping partner in target sellers
              </p>
            </div>

            {/* Overwrite Option */}
            <div className="flex items-center justify-between p-4 border rounded-lg bg-amber-50">
              <div>
                <Label htmlFor="overwrite" className="font-semibold">Overwrite Existing Structures</Label>
                <p className="text-xs text-slate-600 mt-1">
                  If enabled, replaces existing structures for the selected shipping partner
                </p>
              </div>
              <Switch
                id="overwrite"
                checked={overwriteExisting}
                onCheckedChange={setOverwriteExisting}
              />
            </div>

            {/* Summary */}
            <div className="border rounded-lg p-4 bg-blue-50">
              <h4 className="font-semibold text-blue-900 mb-2">Clone Summary</h4>
              <ul className="text-sm text-blue-800 space-y-1">
                <li>• Source: {seller?.brand_name} → {structure?.shipping_partner_name}</li>
                <li>• Target Sellers: {selectedTargetSellers.length} selected</li>
                <li>• Target Partner: {shippingPartners.find(p => p.id === targetShippingPartner)?.name || 'Not selected'}</li>
                <li>• Overwrite: {overwriteExisting ? 'Yes' : 'No'}</li>
              </ul>
            </div>
          </div>

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setShowCloneDialog(false)} disabled={cloning}>
              Cancel
            </Button>
            <Button onClick={handleClone} disabled={cloning} style={{backgroundColor: '#00A699'}}>
              {cloning ? 'Cloning...' : 'Clone Structure'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

// Parameter Configuration Component
const ParameterConfig = ({ param, config, onChange }) => {
  return (
    <div className="border rounded-lg p-4 space-y-3">
      <div className="flex items-center justify-between">
        <h4 className="font-semibold capitalize">{param.replace(/_/g, ' ')}</h4>
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id={`include-pretax-${param}`}
            checked={config.include_in_pretax !== false}
            onChange={(e) => onChange('include_in_pretax', e.target.checked)}
            className="w-4 h-4"
          />
          <Label htmlFor={`include-pretax-${param}`} className="text-xs font-normal cursor-pointer">
            Include in Pre-Tax
          </Label>
        </div>
      </div>
      
      <div className="grid grid-cols-3 gap-3">
        <div className="col-span-2">
          <Label>Method</Label>
          <Select
            value={config.method || "SAME"}
            onValueChange={(value) => onChange('method', value)}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {CALCULATION_METHODS.map(method => (
                <SelectItem key={method.value} value={method.value}>
                  {method.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        {config.method && config.method !== 'SAME' && config.method !== 'CONDITIONAL' && (
          <div>
            <Label>Value</Label>
            <Input
              type="number"
              value={config.value || 0}
              onChange={(e) => onChange('value', parseFloat(e.target.value))}
              step="0.01"
            />
          </div>
        )}
        
        {config.method === 'CALCULATED_FROM_PARAMETER' && (
          <>
            <div>
              <Label>Source Parameter</Label>
              <Select
                value={config.source_parameter || ""}
                onValueChange={(value) => onChange('source_parameter', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select..." />
                </SelectTrigger>
                <SelectContent>
                  {FWD_PARAMETERS.map(p => (
                    <SelectItem key={p} value={p}>{p}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Calculation Type</Label>
              <Select
                value={config.calculation_type || "PERCENTAGE"}
                onValueChange={(value) => onChange('calculation_type', value)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="PERCENTAGE">Percentage</SelectItem>
                  <SelectItem value="FIXED_AMOUNT">Fixed Amount</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </>
        )}
      </div>
      
      {config.method === 'CONDITIONAL' && (
        <div className="mt-4 space-y-3">
          <div className="flex items-center justify-between">
            <Label className="font-semibold">Weight-based Rules</Label>
            <Button
              type="button"
              size="sm"
              onClick={() => {
                const rules = config.rules || [];
                onChange('rules', [...rules, {
                  condition: { parameter: 'charged_wt', operator: '<', value: 500 },
                  calculation: { method: 'ADD_FIXED_TOTAL', value: 0 }
                }]);
              }}
            >
              <Plus className="w-4 h-4 mr-1" />
              Add Rule
            </Button>
          </div>
          
          {(config.rules || []).map((rule, ruleIndex) => (
            <div key={ruleIndex} className="border rounded-lg p-4 bg-slate-50 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-slate-700">Rule {ruleIndex + 1}</span>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    const rules = [...(config.rules || [])];
                    rules.splice(ruleIndex, 1);
                    onChange('rules', rules);
                  }}
                >
                  <Trash2 className="w-4 h-4 text-red-600" />
                </Button>
              </div>
              
              {/* Condition Section */}
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <Label className="text-xs">Parameter</Label>
                  <Input
                    type="text"
                    value={rule.condition?.parameter || 'charged_wt'}
                    disabled
                    className="text-sm bg-slate-100"
                  />
                </div>
                <div>
                  <Label className="text-xs">Operator</Label>
                  <Select
                    value={rule.condition?.operator || '<'}
                    onValueChange={(value) => {
                      const rules = [...(config.rules || [])];
                      rules[ruleIndex].condition.operator = value;
                      onChange('rules', rules);
                    }}
                  >
                    <SelectTrigger className="text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="<">Less than (&lt;)</SelectItem>
                      <SelectItem value="<=">Less than or equal (&lt;=)</SelectItem>
                      <SelectItem value=">">Greater than (&gt;)</SelectItem>
                      <SelectItem value=">=">Greater than or equal (&gt;=)</SelectItem>
                      <SelectItem value="==">Equal (==)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Weight (kg)</Label>
                  <Input
                    type="number"
                    value={rule.condition?.value || 0}
                    onChange={(e) => {
                      const rules = [...(config.rules || [])];
                      rules[ruleIndex].condition.value = parseFloat(e.target.value);
                      onChange('rules', rules);
                    }}
                    step="0.01"
                    className="text-sm"
                  />
                </div>
              </div>
              
              {/* Calculation Section */}
              <div className="grid grid-cols-2 gap-2 pt-2 border-t">
                <div>
                  <Label className="text-xs">Then Calculate</Label>
                  <Select
                    value={rule.calculation?.method || 'ADD_FIXED_TOTAL'}
                    onValueChange={(value) => {
                      const rules = [...(config.rules || [])];
                      rules[ruleIndex].calculation.method = value;
                      onChange('rules', rules);
                    }}
                  >
                    <SelectTrigger className="text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="SAME">Same as SP</SelectItem>
                      <SelectItem value="ADD_PER_KG">Add Per KG</SelectItem>
                      <SelectItem value="MARKUP_PERCENT_TOTAL">Markup % on Total</SelectItem>
                      <SelectItem value="ADD_FIXED_TOTAL">Add Fixed Amount</SelectItem>
                      <SelectItem value="FIXED">Fixed Value</SelectItem>
                      <SelectItem value="MULTIPLY_BY_WEIGHT">Multiply by Weight</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Value</Label>
                  <Input
                    type="number"
                    value={rule.calculation?.value || 0}
                    onChange={(e) => {
                      const rules = [...(config.rules || [])];
                      rules[ruleIndex].calculation.value = parseFloat(e.target.value);
                      onChange('rules', rules);
                    }}
                    step="0.01"
                    className="text-sm"
                    disabled={rule.calculation?.method === 'SAME'}
                  />
                </div>
              </div>
            </div>
          ))}
          
          {(!config.rules || config.rules.length === 0) && (
            <div className="text-sm text-slate-500 text-center py-4 bg-slate-50 rounded border border-dashed">
              No rules defined. Click "Add Rule" to create weight-based conditions.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CommercialStructure;
